import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Container,
  CircularProgress,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Button,
  Fab,
  styled,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import MenuIcon from "@mui/icons-material/Menu";
import PasswordIcon from "@mui/icons-material/Password";
import PaymentIcon from "@mui/icons-material/Payment";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import Person from "@mui/icons-material/Person";
import Info from "@mui/icons-material/Info";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import axios from "axios";
import TopToolbar from "./TopToolbar";
import Footer from "./Footer"; // Fixed typo from "Footr" to "Footer"
import { jsonToHtml } from "./utils/tiptapConfig";
import { useSelector } from "react-redux";

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f5f7fa",
}));

const MainContent = styled(Box)(({ theme, $collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: $collapsed ? "80px" : "280px",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
}));

const MobileToggleButton = styled(IconButton)(({ theme }) => ({
  position: "fixed",
  left: 16,
  top: 16,
  zIndex: theme.zIndex.drawer + 2,
  color: theme.palette.common.white,
  backgroundColor: theme.palette.primary.dark,
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
  },
  boxShadow: theme.shadows[4],
  transition: theme.transitions.create("left", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
}));

// Create a mapping of category names to icons
const categoryIcons = {
  Password: <PasswordIcon fontSize="small" />,
  Account: <Person fontSize="small" />,
  Payments: <PaymentIcon fontSize="small" />,
  General: <Info fontSize="small" />,
  Settings: <SettingsIcon fontSize="small" />,
  // Add more mappings as needed
};

// Default icon if category not found
const DefaultCategoryIcon = <HelpOutlineIcon fontSize="small" />;

const FAQDetailPage = () => {
  // Hooks and state declarations
  const navigate = useNavigate();
  const { faqId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallMobile = useMediaQuery("(max-width:600px)");
  
  const [faq, setFaq] = useState(null);
  const [htmlContent, setHtmlContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [relatedFaqs, setRelatedFaqs] = useState([]);
  const [sidebarExpanded, setSidebarExpanded] = useState(!isMobile);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showSpecificCategory, setShowSpecificCategory] = useState(false);

  // Get data from Redux store
  const { faqs = [] } = useSelector((state) => ({
    faqs: state.support.filteredFaqs || [],
  }));

  // Helper functions
  const findFaqInRedux = (id) => {
    const found = faqs.find((item) => {
      const itemId = item._id || item.id;
      return String(itemId) === String(id);
    });
    return found;
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setSidebarExpanded(!sidebarExpanded);
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  const handleFaqClick = (clickedFaq) => {
    navigate(`/faqs/${clickedFaq._id || clickedFaq.id}`);
  };

  // Effects
  useEffect(() => {
    if (isMobile) {
      setShowSpecificCategory(true);
      setMobileOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const fetchFaq = async () => {
      if (!faqId) return;

      setLoading(true);
      try {
        const response = await axios.get(`/api/support/faq/${faqId}`);
        const fetchedFaq = response.data.faq;

        const reduxFaq = findFaqInRedux(faqId);

        let answerContent = {};
        try {
          answerContent =
            typeof fetchedFaq === "string"
              ? JSON.parse(fetchedFaq)
              : fetchedFaq;
        } catch (e) {
          console.error("Error parsing FAQ answer:", e);
          answerContent = {};
        }

        const mergedFaq = {
          ...reduxFaq,
          answer: answerContent,
          question: reduxFaq?.title || "Untitled FAQ",
          createdBy: reduxFaq?.createdBy || "Unknown Author",
          createdOn: reduxFaq?.createdOn || new Date().toISOString(),
          categoryName: reduxFaq?.categoryName || "General",
        };

        setFaq(mergedFaq);
        const html = jsonToHtml(answerContent);
        setHtmlContent(html);

        if (mergedFaq.categoryName) {
          const related = faqs.filter((item) => {
            const isSameCategory =
              item.categoryName?.toLowerCase() ===
              mergedFaq.categoryName?.toLowerCase();
            const isNotCurrent = String(item.id) !== String(mergedFaq.id);
            return isSameCategory && isNotCurrent;
          });
          setRelatedFaqs(related);
        }
      } catch (error) {
        console.error("Error fetching FAQ:", error);
        const reduxFaq = findFaqInRedux(faqId);
        if (reduxFaq) {
          setFaq({
            ...reduxFaq,
            answer: {},
            question: reduxFaq.title,
            createdBy: reduxFaq.createdBy,
            createdOn: reduxFaq.createdOn,
            categoryName: reduxFaq.categoryName,
          });
          setHtmlContent("");

          if (reduxFaq.categoryName) {
            const related = faqs.filter(
              (item) =>
                item.categoryName === reduxFaq.categoryName &&
                (item._id || item.id) !== faqId
            );
            setRelatedFaqs(related);
          }
        } else {
          setFaq(null);
          setHtmlContent("");
          setRelatedFaqs([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFaq();
  }, [faqId, faqs]);

  if (isMobile && !mobileOpen && !showSpecificCategory) {
    return (
      <MobileToggleButton onClick={toggleSidebar}>
        <MenuIcon />
      </MobileToggleButton>
    );
  }

  return (
    <MainContainer>
      {isMobile && (
        <MobileToggleButton
          onClick={toggleSidebar}
          sx={{ left: mobileOpen ? 240 : 16 }}
        >
          {mobileOpen ? <ChevronLeftIcon /> : <MenuIcon />}
        </MobileToggleButton>
      )}

      <TopToolbar
        expanded={sidebarExpanded}
        toggleExpand={toggleSidebar}
        mobileOpen={mobileOpen}
      />

      <MainContent $collapsed={!sidebarExpanded && !isMobile}>
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            position: "relative",
            zIndex: 1,
            pt: 8,
            pb: 4,
            px: isMobile ? 2 : 4,
            maxWidth: "1400px",
            width: "100%",
            margin: "0 auto",
            gap: isMobile ? 3 : 4,
            background: `
              linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%),
              radial-gradient(circle at 20% 30%, rgba(17, 80, 149, 0.08) 0%, transparent 30%),
              radial-gradient(circle at 80% 70%, rgba(17, 80, 149, 0.05) 0%, transparent 30%)
            `,
          }}
        >
          {/* FAQ Content - Left Side */}
          <Box
            sx={{
              flex: 1,
              bgcolor: "background.paper",
              borderRadius: 4,
              boxShadow: "0 12px 40px -12px rgba(0,0,0,0.15)",
              p: isMobile ? (isSmallMobile ? 2 : 3) : 5,
              border: "1px solid rgba(255,255,255,0.3)",
              background: "rgba(255,255,255,0.85)",
              transition: "all 0.3s ease",
              position: "relative",
              overflow: "hidden",
              maxHeight: isMobile ? "none" : "calc(100vh - 180px)",
              overflowY: isMobile ? "visible" : "auto",
              "&:after": {
                content: '""',
                position: "absolute",
                top: 0,
                right: 0,
                width: "100%",
                height: "4px",
                background: "linear-gradient(90deg, #36a2eb, #4bc0c0)",
                zIndex: 2,
              },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                <CircularProgress size={60} thickness={4} />
              </Box>
            ) : !faq || !htmlContent ? (
              <Typography variant="h5">FAQ not found</Typography>
            ) : (
              <>
                <IconButton
                  aria-label="back"
                  onClick={handleClose}
                  sx={{
                    position: "absolute",
                    right: isSmallMobile ? 8 : 16,
                    top: isSmallMobile ? 8 : 16,
                    color: theme.palette.grey[600],
                    backgroundColor: "rgba(0,0,0,0.03)",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.08)",
                      transform: "translateX(-4px)",
                    },
                    transition: "all 0.3s ease",
                    zIndex: 3,
                  }}
                >
                  <ArrowBack fontSize={isMobile ? "medium" : "large"} />
                </IconButton>

                <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
                  <Typography
                    variant="h3"
                    component="h1"
                    gutterBottom
                    sx={{
                      fontWeight: 800,
                      color: "primary.main",
                      pr: 4,
                      fontSize: isSmallMobile
                        ? "1.75rem"
                        : isMobile
                        ? "2rem"
                        : "2.5rem",
                      lineHeight: 1.2,
                      mb: 3,
                      background: "linear-gradient(90deg, #1976d2, #4facfe)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {faq.question}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 4,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <Chip
                      label={faq.categoryName}
                      color="primary"
                      size={isSmallMobile ? "small" : "medium"}
                      variant="outlined"
                      icon={
                        categoryIcons[faq.categoryName] || DefaultCategoryIcon
                      }
                      sx={{
                        fontWeight: 600,
                        borderWidth: "2px",
                        "& .MuiChip-label": {
                          px: 1.5,
                          ml: 0.5,
                        },
                        borderRadius: 1,
                        "& .MuiChip-icon": {
                          color: "inherit",
                          ml: 0.5,
                        },
                      }}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant={isSmallMobile ? "body2" : "body1"}
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        By IPPIS
                      </Typography>
                      <Box
                        sx={{
                          width: "4px",
                          height: "4px",
                          borderRadius: "50%",
                          bgcolor: "text.secondary",
                          opacity: 0.6,
                        }}
                      />
                      <Typography
                        variant={isSmallMobile ? "body2" : "body1"}
                        color="text.secondary"
                      >
                        {new Date(faq.createdOn).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider
                    sx={{
                      my: 3,
                      borderColor: "divider",
                      borderBottomWidth: "1px",
                      background:
                        "linear-gradient(90deg, transparent, rgba(25, 118, 210, 0.3), transparent)",
                      height: "1px",
                    }}
                  />

                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        "& p": { mb: 2 },
                        "& h1": {
                          fontSize: isSmallMobile ? "1.75rem" : "2rem",
                          mt: 3,
                          mb: 2,
                          fontWeight: 800,
                          color: theme.palette.primary.main,
                        },
                        "& h2": {
                          fontSize: isSmallMobile ? "1.5rem" : "1.75rem",
                          mt: 3,
                          mb: 2,
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                        },
                        "& h3": {
                          fontSize: isSmallMobile ? "1.25rem" : "1.5rem",
                          mt: 3,
                          mb: 2,
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                        },
                        "& ul, & ol": {
                          pl: 3,
                          mb: 2,
                          "& li": { mb: 1 },
                        },
                        "& img": {
                          maxWidth: "100%",
                          height: "auto",
                          borderRadius: 1,
                          boxShadow: theme.shadows[2],
                          display: "block",
                          margin: "1rem auto",
                        },
                        "& table": {
                          borderCollapse: "collapse",
                          width: "100%",
                          margin: "1rem 0",
                          "& th, & td": {
                            border: "1px solid #ddd",
                            padding: "8px",
                            textAlign: "left",
                          },
                          "& th": {
                            backgroundColor: theme.palette.primary.light,
                            color: theme.palette.primary.contrastText,
                          },
                        },
                      }}
                      dangerouslySetInnerHTML={{ __html: htmlContent }}
                    />
                  </Box>
                </Box>
              </>
            )}
          </Box>

          {/* Related FAQs Menu - Right Side on Desktop, Below Content on Mobile */}
          {isMobile ? (
            relatedFaqs.length > 0 && (
              <Box sx={{ width: "100%", mt: 4 }}>
                <Paper
                  elevation={3}
                  sx={{
                    borderRadius: 3,
                    p: isSmallMobile ? 2 : 3,
                    bgcolor: "background.paper",
                    border: "1px solid rgba(255,255,255,0.3)",
                    background: "rgba(255,255,255,0.85)",
                    position: "relative",
                    "&:before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "4px",
                      height: "100%",
                      background:
                        "linear-gradient(to bottom, #36a2eb, #4bc0c0)",
                      borderRadius: "3px 0 0 3px",
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontSize: isSmallMobile ? "1.1rem" : "1.25rem",
                    }}
                  >
                    {faq
                      ? `More FAQs in ${faq.categoryName}`
                      : "Related FAQs"}
                  </Typography>

                  <List dense sx={{ py: 0 }}>
                    {relatedFaqs.map((item) => (
                      <ListItem
                        key={item._id || item.id}
                        disablePadding
                        sx={{
                          mb: 1,
                          borderRadius: 1,
                          overflow: "hidden",
                          bgcolor:
                            (item._id || item.id) === (faq?._id || faq?.id)
                              ? "rgba(25, 118, 210, 0.08)"
                              : "transparent",
                          borderLeft:
                            (item._id || item.id) === (faq?._id || faq?.id)
                              ? "3px solid"
                              : "none",
                          borderColor:
                            (item._id || item.id) === (faq?._id || faq?.id)
                              ? "primary.main"
                              : "transparent",
                        }}
                      >
                        <ListItemButton
                          onClick={() => handleFaqClick(item)}
                          selected={
                            (item._id || item.id) === (faq?._id || faq?.id)
                          }
                          sx={{
                            py: isSmallMobile ? 1 : 1.5,
                            px: isSmallMobile ? 1 : 2,
                            "&.Mui-selected": {
                              bgcolor: "transparent",
                              "&:hover": {
                                bgcolor: "rgba(25, 118, 210, 0.12)",
                              },
                            },
                          }}
                        >
                          <ListItemText
                            primary={item.title || "Untitled FAQ"}
                            primaryTypographyProps={{
                              fontWeight:
                                (item._id || item.id) === (faq?._id || faq?.id)
                                  ? 600
                                  : 500,
                              color:
                                (item._id || item.id) === (faq?._id || faq?.id)
                                  ? "primary.main"
                                  : "text.primary",
                              noWrap: true,
                              textOverflow: "ellipsis",
                              fontSize: isSmallMobile ? "0.9rem" : "1rem",
                            }}
                            secondary={
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  mt: 0.5,
                                }}
                              >
                                {item.categoryName || "General"}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )
          ) : (
            <Box
              sx={{
                width: "350px",
                flexShrink: 0,
                position: "sticky",
                top: "100px",
                alignSelf: "flex-start",
                maxHeight: "calc(100vh - 180px)",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: "3px",
                },
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  borderRadius: 3,
                  p: 3,
                  bgcolor: "background.paper",
                  border: "1px solid rgba(255,255,255,0.3)",
                  background: "rgba(255,255,255,0.85)",
                  position: "relative",
                  "&:before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "4px",
                    height: "100%",
                    background: "linear-gradient(to bottom, #36a2eb, #4bc0c0)",
                    borderRadius: "3px 0 0 3px",
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {faq ? `More FAQs in ${faq.categoryName}` : "Related FAQs"}
                </Typography>

                <List dense sx={{ py: 0 }}>
                  {relatedFaqs.length > 0 ? (
                    relatedFaqs.map((item) => (
                      <ListItem
                        key={item._id || item.id}
                        disablePadding
                        sx={{
                          mb: 1,
                          borderRadius: 1,
                          overflow: "hidden",
                          bgcolor:
                            (item._id || item.id) === (faq?._id || faq?.id)
                              ? "rgba(25, 118, 210, 0.08)"
                              : "transparent",
                          borderLeft:
                            (item._id || item.id) === (faq?._id || faq?.id)
                              ? "3px solid"
                              : "none",
                          borderColor:
                            (item._id || item.id) === (faq?._id || faq?.id)
                              ? "primary.main"
                              : "transparent",
                        }}
                      >
                        <ListItemButton
                          onClick={() => handleFaqClick(item)}
                          selected={
                            (item._id || item.id) === (faq?._id || faq?.id)
                          }
                          sx={{
                            py: 1.5,
                            px: 2,
                            "&.Mui-selected": {
                              bgcolor: "transparent",
                              "&:hover": {
                                bgcolor: "rgba(25, 118, 210, 0.12)",
                              },
                            },
                          }}
                        >
                          <ListItemText
                            primary={item.title || "Untitled FAQ"}
                            primaryTypographyProps={{
                              fontWeight:
                                (item._id || item.id) === (faq?._id || faq?.id)
                                  ? 600
                                  : 500,
                              color:
                                (item._id || item.id) === (faq?._id || faq?.id)
                                  ? "primary.main"
                                  : "text.primary",
                              noWrap: true,
                              textOverflow: "ellipsis",
                            }}
                            secondary={
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  mt: 0.5,
                                }}
                              >
                                {item.categoryName || "General"}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ px: 2, py: 1.5 }}
                    >
                      No related FAQs found
                    </Typography>
                  )}
                </List>
              </Paper>
            </Box>
          )}
        </Container>

        {/* GET SUPPORT Button */}
        {isMobile ? (
          <Fab
            color="primary"
            aria-label="get support"
            onClick={() => navigate("/CreateTicket")}
            sx={{
              position: "fixed",
              top: isSmallMobile ? 16 : 24,
              right: 16,
              zIndex: 1001,
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              "&:hover": {
                transform: "scale(1.1)",
                boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                bgcolor: "#22BB33",
              },
              transition: "all 0.3s ease",
            }}
          >
            <ChatBubbleIcon />
          </Fab>
        ) : (
          <Button
            variant="contained"
            color="primary"
            startIcon={<ChatBubbleIcon />}
            onClick={() => navigate("/CreateTicket")}
            sx={{
              position: "fixed",
              top: 600,
              right: 24,
              zIndex: 1001,
              borderRadius: "999px",
              px: 3,
              py: 1.5,
              fontSize: "0.875rem",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              minWidth: "180px",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                bgcolor: "primary.dark",
              },
              transition: "all 0.3s ease",
            }}
          >
            GET SUPPORT
          </Button>
        )}
      </MainContent>

      {/* Footer */}
      <Footer collapsed={!sidebarExpanded && !isMobile} />
    </MainContainer>
  );
};

export default FAQDetailPage;